'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getMeetupById, getMeetupEvents, deleteMeetup, type Meetup } from '@/services/meetups'
import Button from '@/components/ui/button'
import Alert from '@/components/ui/alert'
import Badge from '@/components/ui/badge'
import Card from '@/components/ui/card'
import Modal from '@/components/ui/modal'
import { formatDate, formatDateTimeRange } from '@/utils/date-utils'
import { supabase } from '@/services/supabase'
import type { Event } from '@/services/events'
import { MeetupSchedulePdfDownload } from '@/components/meetups/MeetupSchedulePdf'
import { getEventParticipantsWithProfiles } from '@/services/event-participants'

// Define the expected structure from getEventParticipants
interface EventParticipantWithProfile {
  id: string
  profile_id: string
  status: string
  is_waitlisted: boolean
  waitlist_position: number | null
  rsvp_notes?: string | null
  profiles: {
    id: string
    username: string | null
    full_name: string | null
    avatar_url: string | null
  }
}

// Define the mapped structure we create for our components
interface MappedEventParticipant {
  id: string
  profile_id: string
  status: string
  is_waitlisted: boolean
  waitlist_position: number | null
  full_name: string | null
  username: string | null
  avatar_url: string | null
}

// Extend the Meetup type to include the host property returned by the API
interface MeetupWithHost extends Omit<Meetup, 'host'> {
  host: {
    id: string
    username: string | null
    full_name: string | null
    avatar_url: string | null
  }
}

// Define an extended event type with additional properties from API responses
interface MeetupEvent extends Omit<Event, 'max_players'> {
  participant_count?: Array<{ count: number }> | null
  max_players?: number | null
  participants?: Array<{
    id: string
    profile_id: string
    status: string
    is_waitlisted: boolean
    waitlist_position: number | null
    full_name: string | null
    username: string | null
    avatar_url: string | null
  }>
}

function MeetupDetailPage() {
  const [meetup, setMeetup] = useState<MeetupWithHost | null>(null)
  const [events, setEvents] = useState<MeetupEvent[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()

  // Get the meetup ID from the URL
  const meetupId = params.id

  // Fetch meetup data
  useEffect(() => {
    async function fetchMeetupData() {
      // Redirect to create page if meetupId is 'new'
      if (meetupId === 'new') {
        navigate('/meetups/create')
        return
      }

      // Skip fetching if no meetupId
      if (!meetupId) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        // Get current user
        const { data: userData } = await supabase.auth.getUser()
        if (userData?.user) {
          setUserId(userData.user.id)
        }

        // Get meetup details
        const { data, error } = await getMeetupById(meetupId)

        if (error) throw new Error(error)
        if (!data) throw new Error('Meetup not found')

        console.log('Meetup data received:', data)
        console.log('Meetup description:', data.description)

        setMeetup(data)

        // Get meetup events
        const { data: eventsData, error: eventsError } = await getMeetupEvents(meetupId)

        if (eventsError) throw new Error(eventsError)

        // Fetch participants for each event
        if (eventsData) {
          const eventsWithParticipants = await Promise.all(
            eventsData.map(async event => {
              const { data: participants } = await getEventParticipantsWithProfiles(event.id)
              return {
                ...event,
                participants,
              }
            })
          )

          setEvents(eventsWithParticipants)
        } else {
          setEvents([])
        }
      } catch (err) {
        console.error('Error fetching meetup details:', err)
        setError(err instanceof Error ? err.message : 'Failed to load meetup details')
      } finally {
        setIsLoading(false)
      }
    }

    fetchMeetupData()
  }, [meetupId, navigate])

  // Handle edit meetup
  const handleEditMeetup = () => {
    navigate(`/meetups/${meetupId}/edit`)
  }

  // Handle delete meetup
  const handleDeleteMeetup = async () => {
    if (!meetupId) return

    setIsDeleting(true)
    setError(null) // Clear any previous errors

    try {
      const { error, success } = await deleteMeetup(meetupId)

      if (error) throw new Error(error)

      if (success) {
        // Close modal and redirect to meetups list
        setIsDeleteModalOpen(false)
        navigate('/meetups')
      } else {
        throw new Error('Failed to delete meetup for unknown reason')
      }
    } catch (err) {
      console.error('Error deleting meetup:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete meetup')
      setIsDeleteModalOpen(false) // Close the modal to show the error
    } finally {
      setIsDeleting(false)
    }
  }

  // Handle create event
  const handleCreateEvent = () => {
    navigate(`/calendar/create?meetupId=${meetupId}`)
  }

  // Handle view calendar
  const handleViewCalendar = () => {
    navigate(`/meetups/${meetupId}/calendar`)
  }

  // Check if user is the host
  const isHost = userId && meetup?.host_id === userId

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Alert variant="error" title="Error">
            {error}
          </Alert>
          <div className="mt-4">
            <Button onClick={() => navigate('/meetups')}>Back to Meetups</Button>
          </div>
        </div>
      </div>
    )
  }

  if (!meetup) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Alert variant="warning" title="Not Found">
            The requested meetup could not be found.
          </Alert>
          <div className="mt-4">
            <Button onClick={() => navigate('/meetups')}>Back to Meetups</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header with cover image */}
        <div className="relative rounded-xl overflow-hidden mb-6">
          {meetup.cover_image_url ? (
            <div className="h-64 w-full relative">
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10"></div>
              <img
                src={meetup.cover_image_url}
                alt={meetup.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 p-6 z-20">
                <h1 className="text-3xl font-bold text-white mb-2">{meetup.title}</h1>
                {meetup.description && (
                  <div className="mb-3">
                    <p className="text-white text-base font-medium border-l-4 border-primary pl-3">
                      {meetup.description}
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {meetup.status === 'draft' && <Badge variant="outline">Draft</Badge>}
                  {meetup.is_private && <Badge variant="outline">Private</Badge>}
                  <span className="text-gray-300 text-sm">
                    {formatDateTimeRange(meetup.start_time, meetup.end_time)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-background p-6 rounded-xl">
              <h1 className="text-3xl font-bold text-white mb-2">{meetup.title}</h1>
              {meetup.description && (
                <div className="mb-3">
                  <p className="text-white text-base font-medium border-l-4 border-primary pl-3">
                    {meetup.description}
                  </p>
                </div>
              )}
              <div className="flex items-center gap-2">
                {meetup.status === 'draft' && <Badge variant="outline">Draft</Badge>}
                {meetup.is_private && <Badge variant="outline">Private</Badge>}
                <span className="text-gray-300 text-sm">
                  {formatDateTimeRange(meetup.start_time, meetup.end_time)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col mb-6">
          {/* View Calendar button - prominent and wide */}
          <div className="w-full flex justify-center mb-4">
            <Button
              variant="primary"
              onClick={handleViewCalendar}
              className="w-full max-w-md text-lg py-3"
            >
              View Calendar
            </Button>
          </div>

          {/* Other action buttons */}
          <div className="flex justify-center gap-4">
            {/* All buttons with equal width */}
            <Button variant="primary" onClick={() => navigate('/meetups')} className="w-40">
              Back to Meetups
            </Button>

            {isHost && (
              <Button variant="primary" onClick={handleEditMeetup} className="w-40">
                Edit Meetup
              </Button>
            )}

            <Button variant="primary" onClick={handleCreateEvent} className="w-40">
              Create Event
            </Button>

            {events.length > 0 && meetup && (
              <div className="w-40 flex justify-center">
                <MeetupSchedulePdfDownload
                  meetup={meetup}
                  events={events}
                  fileName={`${meetup.title} - Event Listing.pdf`}
                />
              </div>
            )}

            {isHost && (
              <Button
                variant="destructive"
                onClick={() => setIsDeleteModalOpen(true)}
                className="w-40"
              >
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Tab content */}
        <div className="bg-background rounded-xl p-6">
          <div className="space-y-6">
            {/* Description */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-2">About this Meetup</h2>
              {meetup.description ? (
                <div className="mb-3">
                  <p className="text-white text-base font-medium border-l-4 border-primary pl-3">
                    {meetup.description}
                  </p>
                </div>
              ) : (
                <p className="text-gray-300">No description provided.</p>
              )}
            </div>

            {/* Location */}
            {(meetup.location || meetup.address) && (
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Location</h2>
                {meetup.location && <p className="text-gray-300 font-medium">{meetup.location}</p>}
                {meetup.address && <p className="text-gray-400">{meetup.address}</p>}
              </div>
            )}

            {/* Host */}
            {meetup.host && (
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Host</h2>
                <div className="flex items-center gap-3">
                  {meetup.host.avatar_url ? (
                    <img
                      src={meetup.host.avatar_url}
                      alt={(meetup.host.username || meetup.host.full_name || '') as string}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-white font-medium">
                        {(meetup.host.username || meetup.host.full_name || 'User')
                          .charAt(0)
                          .toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-white">
                      {meetup.host.username || meetup.host.full_name || 'Anonymous User'}
                    </p>
                    {meetup.host.username && meetup.host.full_name && (
                      <p className="text-sm text-gray-400">{meetup.host.full_name}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {events.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Events</h2>
              <div className="space-y-4">
                {events.map(event => (
                  <Card
                    key={event.id}
                    className="bg-background border-gray-800 hover:border-primary/50 transition-colors"
                    onClick={() => navigate(`/calendar/event/${event.id}`)}
                  >
                    <div className="p-4 cursor-pointer">
                      <div className="flex justify-between items-start">
                        <h3 className="text-lg font-semibold text-white">{event.title}</h3>
                        <Badge variant={event.status === 'scheduled' ? 'primary' : 'outline'}>
                          {event.status.replace('_', ' ')}
                        </Badge>
                      </div>

                      <div className="mt-2 text-sm text-gray-400">
                        <div className="flex items-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>{formatDate(event.start_time)}</span>
                        </div>

                        {event.location && (
                          <div className="flex items-center gap-1 mt-1">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>{event.location}</span>
                          </div>
                        )}
                      </div>

                      {event.participant_count && (
                        <div className="mt-2 text-xs text-gray-400">
                          <span className="font-medium">
                            {event.participant_count[0]?.count || 0}
                          </span>{' '}
                          participants
                          {event.max_players && ` / ${event.max_players} max`}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Meetup"
      >
        <div className="space-y-4">
          <p className="text-gray-300">
            Are you sure you want to delete this meetup? This action cannot be undone.
          </p>

          {events.length > 0 && (
            <Alert variant="warning" title="Warning">
              This meetup has {events.length} event{events.length === 1 ? '' : 's'} associated with
              it. Deleting this meetup will disconnect these events from this meetup, but they will
              still exist in the system as standalone events.
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteMeetup} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete Meetup'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default MeetupDetailPage
